import { Link } from "@/lib/router-compat";
import { Facebook, Instagram, Youtube, Twitter, Rss } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import NewsletterSignup from "@/components/NewsletterSignup";
import { Button } from "@/components/ui/button";
import { usePartsCustomer } from "@/hooks/usePartsCustomer";

const cardIcons = [
  { label: "Visa", bg: "bg-[#1a1f71]", text: "VISA" },
  { label: "Mastercard", bg: "bg-[#eb001b]", text: "MC" },
  { label: "Discover", bg: "bg-[#ff6000]", text: "DISC" },
  { label: "Amex", bg: "bg-[#2e77bb]", text: "AMEX" },
];

type FooterPage = {
  title: string;
  slug: string;
  display_order: number;
  include_in_footer_column_1: boolean;
  include_in_footer_column_2: boolean;
  include_in_footer_column_3: boolean;
};

function PageLinks({ pages }: { pages: FooterPage[] }) {
  if (pages.length === 0) return null;
  return (
    <>
      {pages.map((p) => (
        <li key={p.slug}>
          <Link to={`/parts-catalog/page/${p.slug}`} className="hover:text-primary transition-colors">
            {p.title}
          </Link>
        </li>
      ))}
    </>
  );
}

export default function PartsCatalogFooter() {
  const { user, customer, isVerified, signOut } = usePartsCustomer();
  const isLoggedIn = !!user && !!customer && isVerified;
  const { data: pages = [] } = useQuery({
    queryKey: ["parts_pages_footer"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("parts_pages")
        .select("title, slug, display_order, include_in_footer_column_1, include_in_footer_column_2, include_in_footer_column_3")
        .eq("is_published", true)
        .or("include_in_footer_column_1.eq.true,include_in_footer_column_2.eq.true,include_in_footer_column_3.eq.true")
        .order("display_order", { ascending: true })
        .order("title", { ascending: true });
      if (error) throw error;
      return (data ?? []) as FooterPage[];
    },
    staleTime: 5 * 60 * 1000,
  });

  const col1 = pages.filter((p) => p.include_in_footer_column_1);
  const col2 = pages.filter((p) => p.include_in_footer_column_2);
  const col3 = pages.filter((p) => p.include_in_footer_column_3);

  return (
    <footer className="bg-[#0b0b0b] text-white mt-16 border-t border-white/5">
      <div className="container mx-auto px-4 py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
          {/* Information */}
          <div>
            <h4 className="font-heading text-lg tracking-wide mb-6">Information</h4>
            <ul className="space-y-3 text-sm">
              <PageLinks pages={col1} />
              <li><Link to="/about" className="hover:text-primary transition-colors">About us</Link></li>
              <li><Link to="/contact" className="hover:text-primary transition-colors">Contact us</Link></li>
            </ul>
          </div>

          {/* Customer service */}
          <div>
            <h4 className="font-heading text-lg tracking-wide mb-6">Customer service</h4>
            <ul className="space-y-3 text-sm">
              <li><Link to="/parts-catalog/results" className="hover:text-primary transition-colors">Search</Link></li>
              <li><Link to="/blog" className="hover:text-primary transition-colors">Blog</Link></li>
              <li><Link to="/parts-catalog/results?new=1" className="hover:text-primary transition-colors">New products</Link></li>
              <li><Link to="/parts-catalog/manufacturers" className="hover:text-primary transition-colors">Manufacturers</Link></li>
              <PageLinks pages={col2} />
            </ul>
          </div>

          {/* My account */}
          <div>
            <h4 className="font-heading text-lg tracking-wide mb-6">My account</h4>
            <div className="flex justify-center gap-2 mb-5">
              {isLoggedIn ? (
                <Button asChild size="sm" variant="secondary">
                  <Link to="/parts-catalog/account">Account</Link>
                </Button>
              ) : (
                <>
                  <Button asChild size="sm">
                    <Link to="/parts-catalog/account/login">Log in</Link>
                  </Button>
                  <Button asChild size="sm" variant="secondary">
                    <Link to="/parts-catalog/account/register">Register</Link>
                  </Button>
                </>
              )}
              {isLoggedIn && (
                <Button size="sm" variant="ghost" className="text-white/70 hover:text-white" onClick={signOut}>
                  Sign out
                </Button>
              )}
            </div>
            <ul className="space-y-3 text-sm">
              <li><Link to="/parts-catalog/account" className="hover:text-primary transition-colors">My account</Link></li>
              <li><Link to="/parts-catalog/cart" className="hover:text-primary transition-colors">Cart</Link></li>
              <li><Link to="/parts-catalog/account/orders" className="hover:text-primary transition-colors">Orders</Link></li>
              <li><Link to="/parts-catalog/account/addresses" className="hover:text-primary transition-colors">Addresses</Link></li>
              <li><Link to="/customer/wishlist" className="hover:text-primary transition-colors">Wishlist</Link></li>
              <PageLinks pages={col3} />
            </ul>
          </div>
        </div>

        {/* Bottom row: Follow us / Newsletter / Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mt-14 pt-10 border-t border-white/10 items-start">
          <div className="text-center">
            <h4 className="font-heading text-base mb-4">Follow us</h4>
            <div className="flex justify-center gap-3">
              <a href="https://www.facebook.com/RexAndSonsRV/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="h-10 w-10 rounded-full bg-[#1877f2] flex items-center justify-center hover:opacity-80">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://twitter.com/" target="_blank" rel="noopener noreferrer" aria-label="X" className="h-10 w-10 rounded-full bg-neutral-800 flex items-center justify-center hover:opacity-80">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="/blog/rss.xml" target="_blank" rel="noopener noreferrer" aria-label="RSS" className="h-10 w-10 rounded-full bg-[#ee802f] flex items-center justify-center hover:opacity-80">
                <Rss className="h-5 w-5" />
              </a>
              <a href="https://www.youtube.com/playlist?list=UUOMXdujngOKkWVso3syNywA" target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="h-10 w-10 rounded-full bg-[#ff0000] flex items-center justify-center hover:opacity-80">
                <Youtube className="h-5 w-5" />
              </a>
              <a href="https://www.instagram.com/rexandsons_rv/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="h-10 w-10 rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center hover:opacity-80">
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div className="text-center">
            <h4 className="font-heading text-base mb-4">Newsletter</h4>
            <div className="max-w-sm mx-auto">
              <NewsletterSignup variant="inline" />
            </div>
          </div>

          <div className="text-center">
            <h4 className="font-heading text-base mb-4">Supported cards</h4>
            <div className="flex justify-center gap-2 flex-wrap">
              {cardIcons.map((c) => (
                <div key={c.label} className={`${c.bg} text-white text-[10px] font-bold tracking-wider rounded-sm px-3 py-2 min-w-[56px] text-center`}>
                  {c.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 py-4 text-center text-xs text-white/50">
          © {new Date().getFullYear()} Rex &amp; Sons RV's Inc. Parts Catalog.
        </div>
      </div>
    </footer>
  );
}
